import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import {
    EntityChangeTrackingDto,
    IChangeTrackingFacade,
    IEntity,
    SelectOption,
} from '@pf/shared-common';
import { getChangedFieldNames } from '@pf/shared/util-platform';

import { IEntityHistoryComponent } from './IEntityHistoryComponent';
import { BehaviorSubject, combineLatest, map, Observable, tap } from 'rxjs';
import { EntityHistoryDrawerComponent } from './entity-history-drawer.component';
import { calcDrawerHeight } from '@pf/shared-ui';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { DetailedHistoryViewComponent } from './detailed-history-view.component';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'platform-entity-history',
    templateUrl: './entity-history.component.html',
    styleUrls: ['./entity-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EntityHistoryComponent
    implements IEntityHistoryComponent<IEntity>, OnInit
{
    private cdr = inject(ChangeDetectorRef);
    private drawerService = inject(NzDrawerService);

    @Input() facade!: IChangeTrackingFacade<IEntity>;
    @Input() entityName!: string;
    recentChanges$!: Observable<EntityChangeTrackingDto<IEntity>[]>;
    filteredChanges$!: Observable<EntityChangeTrackingDto<IEntity>[]>;
    availableFieldOptions: SelectOption[] = [];
    selectedFields$ = new BehaviorSubject<string[]>([]);
    drawerHeight: string;
    protected changeFilterControl = new FormControl<string[]>([]);

    get detailedHistoryTitle() {
        return `${this.entityName} Detailed History`;
    }

    constructor() {
        const historyDrawer = inject(EntityHistoryDrawerComponent, {
            optional: true,
            host: true,
        });

        this.drawerHeight = calcDrawerHeight(historyDrawer ? 1 : 0);
    }

    ngOnInit(): void {
        if (!this.facade) {
            throw new Error('Facade must be set on entity history component');
        }
        this.recentChanges$ = this.facade
            .getRecentChanges$()
            .pipe(tap(changes => this.buildAvailableFields(changes)));
        this.filteredChanges$ = combineLatest([
            this.recentChanges$,
            this.selectedFields$,
        ]).pipe(
            map(([changes, selectedFields]) => {
                if (!selectedFields.length) {
                    return changes;
                }
                return changes.filter(change =>
                    (change.changes || []).some(op =>
                        getChangedFieldNames([op]).some(field =>
                            selectedFields.includes(field)
                        )
                    )
                );
            })
        );
    }

    onFieldFilterChange(options: SelectOption[]) {
        this.selectedFields$.next(options.map(o => o.value as string));
    }

    private buildAvailableFields(changes: EntityChangeTrackingDto<IEntity>[]) {
        const fieldSet = new Set<string>();
        for (const change of changes) {
            for (const field of getChangedFieldNames(change.changes || [])) {
                fieldSet.add(field);
            }
        }
        this.availableFieldOptions = Array.from(fieldSet)
            .sort()
            .map(field => ({ label: field, value: field }));
    }

    trackByFn(_: number, value: EntityChangeTrackingDto<IEntity>) {
        return value.id;
    }

    openDetailedHistory() {
        const drawerRef = this.drawerService.create({
            nzTitle: this.detailedHistoryTitle,
            nzContent: DetailedHistoryViewComponent,
            nzHeight: this.drawerHeight,
            nzPlacement: 'bottom',
            nzData: {
                changeTrackingFacade: this.facade,
                entityName: this.entityName,
            },
        });

        const detectChanges = () => {
            setTimeout(() => {
                this.cdr.detectChanges();
            }, 1000);
        };

        detectChanges();

        drawerRef.afterClose.subscribe(_ => {
            detectChanges();
        });
    }
}
